/**
 * TopNav client selector — uses the lean /agents/picker endpoint (4 fields:
 * agent_id, hostname, status, platform) for fast loading.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@features/auth/AuthContext";
import { usePickerAgents, useSelectedClient } from "@features/endpoint-inventory/hooks/useRegistry";
import { useEffectiveOrgId } from "@features/endpoint-inventory/hooks/useOrgPicker";
import type { PickerAgent } from "@features/endpoint-inventory/api/registry.schemas";
import styles from "./ClientSelector.module.scss";

/**
 * Optional controlled-mode props. When supplied (by TopNav), open/close is
 * driven externally so the parent can keep this and the Browse panel
 * mutually exclusive. Omitted → fall back to internal state.
 */
interface ClientSelectorProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({ isOpen, onOpenChange }) => {
  const { user } = useAuth();
  // Group admins have no fixed org_id on their JWT — they get one by
  // picking from the OrgSelector, which writes to localStorage and is
  // read reactively here via useEffectiveOrgId. Org admins get their
  // JWT org and this is a no-op for them.
  const orgId = useEffectiveOrgId();
  // Silence the unused-user lint since AuthContext is now consumed via
  // useEffectiveOrgId instead of here.
  void user;
  const { selected, select } = useSelectedClient();

  // Controlled when parent passes isOpen+onOpenChange, uncontrolled otherwise.
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen ?? internalOpen;
  const setOpen = (next: boolean | ((o: boolean) => boolean)): void => {
    const value = typeof next === "function" ? next(open) : next;
    if (isOpen !== undefined) onOpenChange?.(value);
    else setInternalOpen(value);
  };

  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Use the picker endpoint — only 4 fields, sorted by last_seen desc.
  const list = usePickerAgents(orgId, search || undefined);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 60);
    else setSearch("");
  }, [open]);

  const agents = useMemo<PickerAgent[]>(() => {
    const rows = list.data ?? [];

    // Client-side filter that matches hostname OR private_ip, so a user
    // typing "10.0.0." narrows the list immediately. The server's own
    // ?search= param is still sent (for server-side pre-filtering on
    // large fleets), but we re-filter locally here as a safety net so
    // IP search works even if the backend's search only looks at
    // hostname. For the ≤50 rows the picker returns this is instant.
    const q = search.trim().toLowerCase();
    const filtered = q === ""
      ? rows
      : rows.filter((a) =>
          a.hostname.toLowerCase().includes(q) ||
          (a.private_ip ?? "").toLowerCase().includes(q),
        );

    // Float the currently-selected agent to the top of whatever's left.
    if (!selected?.agent_id) return filtered;
    const cur = filtered.find((a) => a.agent_id === selected.agent_id);
    if (!cur) return filtered;
    return [cur, ...filtered.filter((a) => a.agent_id !== selected.agent_id)];
  }, [list.data, selected, search]);

  const pick = (a: PickerAgent) => {
    select({ agent_id: a.agent_id, hostname: a.hostname });
    setOpen(false);
  };

  if (!orgId) return null;

  return (
    <div className={styles.wrap} ref={rootRef}>
      <button
        className={`${styles.crumb} ${open ? styles.open : ""} ${selected ? styles.picked : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selected ? selected.hostname : "Pick a client"}
      >
        <i className={`fas ${selected ? "fa-server" : "fa-plug-circle-plus"} ${styles.icon}`} />
        {selected ? (
          <span className={styles.host}>{selected.hostname}</span>
        ) : (
          <span className={styles.pickText}>Pick a client</span>
        )}
        <i className={`fas fa-chevron-down ${styles.caret}`} />
      </button>

      {open && (
        <div className={styles.dropdown} role="listbox">
          <div className={styles.search}>
            <i className="fas fa-magnifying-glass" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search hostname or IP…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.list}>
            {list.isFetching && !list.data && (
              <div className={styles.state}>Loading…</div>
            )}
            {list.error && !list.data && (
              <div className={styles.state}>Couldn't load clients.</div>
            )}
            {list.data && agents.length === 0 && (
              <div className={styles.state}>
                {search ? `No clients match "${search}"` : "No clients enrolled yet."}
              </div>
            )}
            {agents.map((a) => {
              const isSelected = selected?.agent_id === a.agent_id;
              return (
                <button
                  key={a.agent_id}
                  className={`${styles.row} ${isSelected ? styles.rowSelected : ""}`}
                  onClick={() => pick(a)}
                >
                  <span className={`${styles.statusDot} ${a.status === "active" ? styles.on : styles.off}`} />
                  <div className={styles.rowMain}>
                    <span className={styles.rowHost}>{a.hostname}</span>
                    {/* Meta line — platform plus the private IP when the
                        backend reports one. Joining with " · " matches the
                        existing look used elsewhere in the TopNav (compact,
                        dot-separated secondary info). IP falls back silently
                        when absent so older picker responses still look
                        correct. */}
                    <span className={styles.rowMeta}>
                      {[a.platform ?? a.status, a.private_ip]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                  {isSelected && <i className={`fas fa-check ${styles.check}`} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
